import { useCallback, useState, useMemo } from 'react';
import { useQuestionSaveResult } from 'src/hooks/job';
import { notification } from 'src/components/Notification';
import { useTranslation } from 'react-i18next';
import { Mutex } from 'async-mutex';

import flatten from 'flat-util';
import { sanitizeQuestionResult } from 'src/utils/helpers';
import { NOT_YET_STARTED } from 'src/utils/data/job';

export default (
  jobId,
  // eslint-disable-next-line no-unused-vars
  { reloadJobChecklistResults, reloadJobDatasheetResults, reloadJob, job },
) => {
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);

  const mutex = useMemo(() => new Mutex(), [jobId]);

  const saveResults = useQuestionSaveResult();
  const saveJobChecklistResultsCallback = useCallback(
    async (checklistId, checklistVersion, data, saveResultCallback, promptError = true) => {
      await mutex.runExclusive(async () => {
        setIsLoading(true);
        const checklistQuestionResult = flatten([data]
          .map(
            ({
              attachments,
              correctiveAction,
              customPanelResults,
              nonCompliances,
              preventiveAction,
              responsiblePerson,
              rootCause,
              targetDate,
              attachmentsMeta,
              ...d
            }) => ({
              ...d,
              checklistId,
              checklistVersion,
              customPanelResults: customPanelResults?.map(result => ({
                currentQuestionId: result.currentQuestionId,
                customPanelId: result.customPanelId,
                customPanelVersionId: result.customPanelVersionId,
                datasheetQuestionId: result.datasheetQuestionId,
                result: result.result,
              })),
              attachmentsMeta: (attachmentsMeta || []).map(({ __typename, ...rest }) => rest),
              nonCompliances: nonCompliances.map(x => {
                const { __typename, ...rest } = x;
                return {
                  ...rest,
                  id: x.nonComplianceId,
                  nonComplianceId: undefined,
                  checklistQuestionResultId: undefined,
                };
              }) || [],
              attachmentIds: (attachments || []).map(x => x.attachmentId),
            }),
          )
          .map(x => {
            const { __typename, ...rest } = x;
            return {
              ...rest,
              id: undefined,
            };
          }), true)[0];

        // eslint-disable-next-line no-underscore-dangle
        delete checklistQuestionResult.__typename;
        delete checklistQuestionResult.isMissingResults;

        try {
          const response = await saveResults({ jobId, checklistQuestionResult });
          if (!response.data && promptError) {
            notification.error(t('SAVE_ERROR'));
          }
          saveResultCallback(data, false);
        } catch (e) {
          if (promptError) {
            notification.error(t('SAVE_ERROR'));
          }
          const { networkError } = e;
          if (networkError && networkError.name !== 'ServerError') {
            saveResultCallback(data, true);
          }
        }

        await reloadJobChecklistResults();
        setIsLoading(false);
      });
    },
    [jobId],
  );

  const saveJobDatasheetResultsCallback = useCallback(
    async (datasheetId, datasheetVersion, data, saveResultCallback, promptError = true, removeResultQuestionIds = []) => {
      await mutex.runExclusive(async () => {
        setIsLoading(true);
        const datasheetQuestionResult = flatten([data]
          .map(
            ({
              attachments,
              attachmentsMeta,
              externalAttachments,
              externalAttachmentsMeta,
              ...d
            }) => ({
              ...d,
              datasheetId,
              datasheetVersion,
              removeResultQuestionIds,
              attachmentIds:
                attachments.length > 0
                  ? attachments.map(x => {
                    return x.attachmentId;
                  })
                  : [],
              attachmentsMeta : (attachmentsMeta || []).map(x => {
                // eslint-disable-next-line no-param-reassign,no-underscore-dangle
                const { __typename, ...rest } = x;
                return rest;
              }),
              externalAttachmentIds:
                (externalAttachments || []).length > 0
                  ? externalAttachments.map(x => {
                    return x.attachmentId;
                  })
                  : [],
              externalAttachmentsMeta : (externalAttachmentsMeta || []).map(x => {
                // eslint-disable-next-line no-param-reassign,no-underscore-dangle
                const { __typename, ...rest } = x;
                return rest;
              }),
            }))
          .map(x => {
            // eslint-disable-next-line no-param-reassign,no-underscore-dangle
            delete x.__typename;
            // eslint-disable-next-line no-param-reassign
            x.result = sanitizeQuestionResult(x.result, x.result?.type);
            return {
              ...x,
              id: undefined,
            };
          }), true)[0];

        // eslint-disable-next-line no-underscore-dangle
        delete datasheetQuestionResult.__typename;

        try {
          const response = await saveResults({ jobId, datasheetQuestionResult });
          if(!(response.data?.saveQuestionResult?.success) && response.data?.saveQuestionResult?.error){
              notification.onErrorWithOK(response.data?.saveQuestionResult?.error, t('OK'));
          }else if ((!response.data && promptError) || !(response.data?.saveQuestionResult?.success)) {
            notification.error(t('SAVE_ERROR'));
          }          
          saveResultCallback(data, true);
        } catch (e) {
          if (promptError) {
            notification.error(t('SAVE_ERROR'));
          }
          const { networkError } = e;
          if (networkError && networkError.name !== 'ServerError') {
            saveResultCallback(data, false);
          }
        }
        await reloadJobDatasheetResults();
        // when user starts typing in the datasheet and job status is not_yet_started
        // reload the job to get in_process status for new SAQ process
        if (job.status === NOT_YET_STARTED) {
          await reloadJob();
        }
        setIsLoading(false);
      });
    },
    [jobId],
  );

  return {
    isLoading,
    saveJobChecklistResults: saveJobChecklistResultsCallback,
    saveJobDatasheetResults: saveJobDatasheetResultsCallback,
  };
};
